import React, { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Alert, Button, Dialog, DialogContent, DialogTitle, IconButton, Step, StepLabel, Stepper } from '@mui/material';

import { useSaveOfferBuilderRoom } from '~/hooks/apis/accommodation/useSaveOfferBuilderRoom';
import { useCreateBatchRoomRates } from '~/hooks/apis/reservation/useCreateBatchRoomRates';
import { useCreateRoomTypeCapacity } from '~/hooks/apis/reservation/useCreateRoomTypeCapacity';

import { reportError } from '~/utils/reportError';

import { useCapacityStep } from './CapacityStep/useCapacityStep';
import ExitConfirmationDialog from './ExitConfirmationDialog';
import { usePackageSetupStep } from './PackageSetupStep/usePackageSetupStep';
import { useOpportunityStep } from './PrerequisitesStep/useOpportunityStep';
import { usePropertyStep } from './PropertyStep/usePropertyStep';
import { useRatePlanStep } from './RatePlanStep/useRatePlanStep';
import { useRoomRatesStep } from './RoomRatesStep/useRoomRatesStep';
import { useRoomsStep } from './RoomsStep/useRoomsStep';
import { useSubmissionStep } from './SubmissionStep/useSubmissionStep';
import { useTaxesFeesStep } from './TaxesFeesStep/useTaxesFeesStep';
import WizardActions from './WizardActions';
import WizardStepContent from './WizardStepContent';
import { getActiveStepFlags } from './activeStepFlags';
import { CREATE_NEW_PROPERTY, STEPS } from './constants';
import type { SetUpFlashOfferSectionProps } from './types';
import { usePropertyCreateHandoff } from './usePropertyCreateHandoff';
import { useWizardButtonState } from './useWizardButtonState';
import { useWizardDialog } from './useWizardDialog';
import { useWizardNavigation } from './useWizardNavigation';

export default function SetUpFlashOfferSection({
  disabled,
  onPropertiesRefresh,
  properties = [],
  vendor,
  vendorId,
}: SetUpFlashOfferSectionProps) {
  const defaultPropertyId = properties.find((property) => !property.deleted_at)?.id ?? CREATE_NEW_PROPERTY;
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [propertyWarningMessage, setPropertyWarningMessage] = useState<string | null>(null);
  const [isAdvancingStep, setIsAdvancingStep] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const {
    isContentStep,
    isPackageSetupStep,
    isPrerequisitesStep,
    isPropertyStep,
    isRatePlanStep,
    isRoomRatesStep,
    isRoomStep,
    isSubmissionStep,
    isTaxesAndFeesStep,
  } = getActiveStepFlags(activeStep);
  const opportunity = useOpportunityStep({ isPrerequisitesStep, vendorId });
  const { trimmedSalesforceOfferId } = opportunity;
  const propertyStep = usePropertyStep({ defaultPropertyId, isPropertyStep, properties });
  const {
    isCreatePropertyStep,
    isPropertyStepNextDisabled,
    selectedProperty,
    selectedPropertyHasRooms,
    selectedPropertyRoomTypes,
  } = propertyStep;
  const saveRoom = useSaveOfferBuilderRoom();
  const roomsStep = useRoomsStep({
    isRoomStep,
    offerId: trimmedSalesforceOfferId,
    saveRoom: saveRoom.mutateAsync,
    selectedProperty,
  });
  const { aiRoomNameOverrides, isRoomStepNextDisabled, roomDrafts, salesforceRooms } = roomsStep;
  const createBatchRoomRates = useCreateBatchRoomRates();
  const createRoomTypeCapacity = useCreateRoomTypeCapacity();
  const ratePlanStep = useRatePlanStep({ isRatePlanStep, vendorId });
  const { createdRatePlans, isRatePlanStepNextDisabled, ratePlanDrafts } = ratePlanStep;
  const roomRatesStep = useRoomRatesStep({
    createBatchRoomRates: createBatchRoomRates.mutateAsync,
    createdRatePlans,
    isRoomRatesStep,
    propertyId: selectedProperty?.id,
    ratePlanDrafts,
    roomDrafts,
    roomTypes: selectedPropertyRoomTypes,
  });
  const { draftRoomRateAssignments, isRoomRatesStepNextDisabled, roomRateRooms } = roomRatesStep;
  const capacityStep = useCapacityStep({
    createRoomTypeCapacity: createRoomTypeCapacity.mutateAsync,
    propertyId: selectedProperty?.id,
  });
  const packageSetupStep = usePackageSetupStep({
    aiRoomNameOverrides,
    appliedFlashRoomRates: draftRoomRateAssignments,
    isPackageSetupStep,
    propertyId: selectedProperty?.id,
    roomTypes: roomRateRooms,
    salesforceRooms,
    setIsAdvancingStep,
    trimmedSalesforceOfferId,
  });
  const { isPackageSetupStepNextDisabled } = packageSetupStep;
  const taxesFeesStep = useTaxesFeesStep({ isTaxesAndFeesStep, propertyId: selectedProperty?.id });
  const submissionStep = useSubmissionStep({
    aiRoomNameOverrides,
    capacityStep,
    packageSetupStep,
    propertyStep,
    ratePlanStep,
    roomRatesStep,
    roomsStep,
    salesforceRooms,
    taxesFeesStep,
    trimmedSalesforceOfferId,
  });

  const propertyCreateHandoff = usePropertyCreateHandoff({
    onPropertiesRefresh,
    propertyStep,
    roomsStep,
    setActiveStep,
    setErrorMessage,
    setIsAdvancingStep,
  });

  const { isStepBusy, primaryButtonLabel } = useWizardButtonState({
    activeStep,
    capacityStep,
    isAdvancingStep,
    opportunity,
    propertyStep,
    ratePlanStep,
    roomRatesStep,
    roomsStep,
    submissionStep,
  });

  const wizardNavigation = useWizardNavigation({
    activeStep,
    isStepBusy,
    opportunity,
    propertyStep,
    ratePlanStep,
    refreshProperties: propertyCreateHandoff.refreshProperties,
    roomsStep,
    setActiveStep,
    setErrorMessage,
    setIsAdvancingStep,
    setPropertyWarningMessage,
    vendor,
    vendorId,
  });

  const wizardDialog = useWizardDialog({
    activeStep,
    capacityStep,
    isStepBusy,
    opportunity,
    packageSetupStep,
    propertyStep,
    ratePlanStep,
    roomRatesStep,
    roomsStep,
    selectedPropertyHasRooms,
    setActiveStep,
    setErrorMessage,
    setIsAdvancingStep,
    setPropertyWarningMessage,
    submissionStep,
    taxesFeesStep,
  });
  const { contentPageUrl, goToContentPage, handleOpen } = wizardDialog;

  async function handleSubmitFlashOffer() {
    const result = await submissionStep.submit();

    if (result.success) {
      // Only refetched once the whole submission is confirmed, never mid-attempt -- a later
      // stage's failure would roll the rooms this just fetched back.
      try {
        await onPropertiesRefresh?.(selectedProperty ?? undefined);
      } catch (error) {
        reportError(error);
      }

      setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1));
    }
  }

  return (
    <>
      <Button variant="contained" onClick={handleOpen} disabled={disabled}>
        Set up Flash offer
      </Button>

      <Dialog open={wizardDialog.isDialogOpen} onClose={wizardDialog.handleClose} fullWidth maxWidth="md">
        <DialogTitle sx={{ textAlign: 'center' }}>
          Set up Flash offer
          <IconButton
            aria-label="Close"
            disabled={isStepBusy}
            onClick={wizardDialog.handleClose}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
            {STEPS.map((step) => (
              <Step key={step.label}>
                <StepLabel>{step.label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {propertyWarningMessage && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {propertyWarningMessage}
            </Alert>
          )}

          <WizardStepContent
            activeStep={activeStep}
            errorMessage={errorMessage}
            isStepBusy={isStepBusy}
            onAdvanceStep={() => setActiveStep((prev) => Math.min(prev + 1, STEPS.length - 1))}
            onGoToContentPage={goToContentPage}
            onGoToStep={setActiveStep}
            onPropertyCreateError={propertyCreateHandoff.handlePropertyCreateError}
            onPropertyCreateStart={propertyCreateHandoff.handlePropertyCreateStart}
            onPropertyCreateSuccess={propertyCreateHandoff.handlePropertyCreateSuccess}
            onRetrySync={wizardDialog.retryResync}
            onSalesforceIdChange={(value) => {
              opportunity.setSalesforceOfferId(value);
              ratePlanStep.initializeRatePlanDrafts();
              setErrorMessage(null);
            }}
            opportunity={opportunity}
            packageSetupStep={packageSetupStep}
            propertyStep={propertyStep}
            ratePlanStep={ratePlanStep}
            roomRatesStep={roomRatesStep}
            roomsStep={roomsStep}
            setErrorMessage={setErrorMessage}
            submissionStep={submissionStep}
            taxesFeesStep={taxesFeesStep}
            trimmedSalesforceOfferId={trimmedSalesforceOfferId}
            vendorId={vendorId}
          />
        </DialogContent>
        <WizardActions
          activeStep={activeStep}
          contentPageUrl={contentPageUrl}
          isContentStep={isContentStep}
          isCreatePropertyStep={isCreatePropertyStep}
          isPackageSetupStep={isPackageSetupStep}
          isPackageSetupStepNextDisabled={isPackageSetupStepNextDisabled}
          isPrerequisitesStepNextDisabled={opportunity.isPrerequisitesStepNextDisabled}
          isPropertyStepNextDisabled={isPropertyStepNextDisabled}
          isRatePlanStep={isRatePlanStep}
          isRatePlanStepNextDisabled={isRatePlanStepNextDisabled}
          isRoomRatesStepNextDisabled={isRoomRatesStepNextDisabled}
          isRoomStepNextDisabled={isRoomStepNextDisabled}
          isStepBusy={isStepBusy}
          isSubmissionStep={isSubmissionStep}
          isSubmissionStepNextDisabled={submissionStep.hasUnresolvedRollbackErrors}
          isTaxesAndFeesStep={isTaxesAndFeesStep}
          onGoToContentPage={goToContentPage}
          onNext={wizardNavigation.handleNext}
          onPrevious={wizardDialog.handlePrevious}
          onSubmit={handleSubmitFlashOffer}
          primaryButtonLabel={primaryButtonLabel}
          trimmedSalesforceOfferId={trimmedSalesforceOfferId}
        />
      </Dialog>

      <ExitConfirmationDialog
        onCancel={wizardDialog.handleCancelClose}
        onConfirm={wizardDialog.handleConfirmClose}
        open={wizardDialog.isExitConfirmationOpen}
      />
    </>
  );
}
